import { useTheme, Box, Stack } from "@mui/material";
import dynamic from "next/dynamic";

import { useLiveFormEditorContext } from "../../common/LiveFormEditorContext";

import { sanitizeHtmlString } from "@/common/helpers/html";
import { DashboardSkeleton } from "@/dashboard-design-system/Skeleton";
import { EmailContentPreview } from "@/features/LiveFormEditor/LivePreview/EmailPreview/EmailContentPreview/EmailContentPreview";

const DonationThankYouPreview = dynamic(
  () => import("./DonationThankYouPreview/DonationThankYouPreview").then((m) => m.DonationThankYouPreview),
  { ssr: false, loading: () => <DashboardSkeleton variant="rounded" width="100%" height="100%" /> }
);

const FundraiserThankYouPreview = dynamic(
  () => import("./FundraiserThankYouPreview/FundraiserThankYouPreview").then((m) => m.FundraiserThankYouPreview),
  { ssr: false, loading: () => <DashboardSkeleton variant="rounded" width="100%" height="100%" /> }
);

export const DonationEmailLivePreview = () => {
  const theme = useTheme();
  const { liveFormData } = useLiveFormEditorContext<"donation-form">();
  const emailInPreview = liveFormData?.automatedCommunicationsData.emailInPreview;
  const isAIDefaultLoading = liveFormData?.automatedCommunicationsData.isAIDefaultLoading ?? false;
  const isFundraiserEmail = emailInPreview === "thank-you-email-fundraiser";

  if (emailInPreview === "thank-you-email" || isFundraiserEmail) {
    return (
      <Box
        sx={{
          height: "100%",
          width: "100%",
          backgroundColor: (theme) => theme.palette.surface.neutral.supershy,
          overflowY: "auto",
        }}
        data-test="live-preview"
      >
        {isFundraiserEmail ? <FundraiserThankYouPreview /> : <DonationThankYouPreview />}
      </Box>
    );
  }

  const body =
    emailInPreview === "auto-renewal" ? (liveFormData?.automatedCommunicationsData.autoRenewal?.body ?? "") : "";
  const trimmedText = sanitizeHtmlString(body, { preserveEmptyParagraphs: true }) || "";

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      sx={{
        height: "100%",
        backgroundColor: (theme) => theme.palette.surface.neutral.supershy,
        overflowY: "auto",
      }}
      data-test="live-preview"
    >
      <Stack spacing={2} sx={{ marginY: { xs: 0, md: 10 }, marginX: { xs: 2, md: 15 } }}>
        <Stack
          spacing={2}
          id="live-preview-email"
          sx={{ backgroundColor: theme.palette.background.default, borderRadius: 1, padding: 3 }}
        >
          {isAIDefaultLoading ? (
            <Stack gap={1}>
              <DashboardSkeleton variant="rounded" width="100%" height={16} />
              <DashboardSkeleton variant="rounded" width="80%" height={16} />
              <DashboardSkeleton variant="rounded" width="60%" height={16} />
            </Stack>
          ) : (
            <EmailContentPreview html={trimmedText} data-test="email-live-preview-content" />
          )}
        </Stack>
      </Stack>
    </Box>
  );
};
